// ./src/components/features/AssignContentBlockToPlaylistModal/AssignContentBlockToPlaylistModal.tsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Stack,
  Text,
  Group,
  Checkbox,
  ScrollArea,
  Alert,
  Box,
  Divider,
} from "@mantine/core";
import { AppModal, AppIcon, LoadingIndicator } from "@/components/generic";
import { ContentTypeBadge } from "@/components/features";
import { ContentBlock } from "@/utils/api";
import { usePlayLists, usePlayListItems } from "@/hooks";
import { formatDate, formatTimeRange, formatDaysOfWeek } from "@/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useApp } from "@/contexts";

dayjs.extend(utc);

export interface AssignContentBlockToPlaylistModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Content block to assign */
  contentBlock: ContentBlock;
}

interface PlaylistOperationState {
  playlistId: string;
  loading: boolean;
  error: string | null;
}

export const AssignContentBlockToPlaylistModal: React.FC<
  AssignContentBlockToPlaylistModalProps
> = ({ opened, onClose, contentBlock }) => {
  // State for tracking operations per playlist
  const [operations, setOperations] = useState<
    Record<string, PlaylistOperationState>
  >({});

  const { isAuthenticated } = useApp();

  // Fetch all playlists
  const { playLists, loading: playListsLoading } = usePlayLists({
    autoLoad: true,
    isAuthenticated,
  });

  // Fetch playlist items for this content block
  const {
    playListItems,
    loading: playListItemsLoading,
    create: createPlayListItem,
    remove: removePlayListItem,
    list: listPlayListItems,
  } = usePlayListItems({
    autoLoad: false,
  });

  // Load playlist items when modal opens
  useEffect(() => {
    if (opened && contentBlock.id) {
      listPlayListItems(
        { contentBlockId: [contentBlock.id] },
        { page: 1, pageSize: 1000 },
        undefined,
        true
      );
    }
  }, [opened, contentBlock.id, listPlayListItems]);

  // Create a set of assigned playlist IDs for quick lookup
  const assignedPlaylistIds = useMemo(() => {
    return new Set(
      playListItems
        .filter((item) => item.playListId)
        .map((item) => item.playListId as string)
    );
  }, [playListItems]);

  // Get playlist item ID for a playlist (for removal)
  const getPlayListItemId = (playlistId: string): string | null => {
    const item = playListItems.find((item) => item.playListId === playlistId);
    return item?.id || null;
  };

  // Reset operations state when modal closes
  useEffect(() => {
    if (!opened) {
      setOperations({});
    }
  }, [opened]);

  // Handle checkbox change
  const handleCheckboxChange = async (playlistId: string, checked: boolean) => {
    // Set loading state for this playlist
    setOperations((prev) => ({
      ...prev,
      [playlistId]: {
        playlistId,
        loading: true,
        error: null,
      },
    }));

    try {
      if (checked) {
        // Create playlist item
        const result = await createPlayListItem({
          playListId: playlistId,
          contentBlockId: contentBlock.id,
        });

        if (result) {
          // Success - clear operation state
          setOperations((prev) => {
            const newOps = { ...prev };
            delete newOps[playlistId];
            return newOps;
          });
        } else {
          // Error - show error message
          setOperations((prev) => ({
            ...prev,
            [playlistId]: {
              playlistId,
              loading: false,
              error: "Failed to add content block to playlist",
            },
          }));
        }
      } else {
        // Remove playlist item
        const playListItemId = getPlayListItemId(playlistId);
        if (playListItemId) {
          const success = await removePlayListItem(playListItemId);

          if (success) {
            // Success - clear operation state
            setOperations((prev) => {
              const newOps = { ...prev };
              delete newOps[playlistId];
              return newOps;
            });
          } else {
            // Error - show error message
            setOperations((prev) => ({
              ...prev,
              [playlistId]: {
                playlistId,
                loading: false,
                error: "Failed to remove from playlist",
              },
            }));
          }
        }
      }
    } catch (error) {
      setOperations((prev) => ({
        ...prev,
        [playlistId]: {
          playlistId,
          loading: false,
          error: error instanceof Error ? error.message : "Operation failed",
        },
      }));
    }
  };

  // Clear error for a specific playlist
  const clearPlaylistError = (playlistId: string) => {
    setOperations((prev) => {
      const newOps = { ...prev };
      delete newOps[playlistId];
      return newOps;
    });
  };

  const isLoading = playListsLoading || playListItemsLoading;

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title="Add Content Block to Playlists"
      size="lg"
      actionType="close"
    >
      <Stack gap="md">
        {/* Content Block Info */}
        <Box>
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <Text size="sm" fw={600}>
                {contentBlock.name || "Untitled Content Block"}
              </Text>
              <ContentTypeBadge type={contentBlock.contentType as any} />
            </Group>

            {contentBlock.description && (
              <Text size="sm" c="dimmed">
                {contentBlock.description}
              </Text>
            )}

            {/* Schedule Info */}
            {(contentBlock.startDatetime ||
              contentBlock.endDatetime ||
              contentBlock.startTime ||
              contentBlock.endTime ||
              contentBlock.recurrencePattern) && (
              <Stack gap={4}>
                <Text size="xs" c="dimmed" fw={500}>
                  Schedule:
                </Text>
                {(contentBlock.startDatetime || contentBlock.endDatetime) && (
                  <Text size="xs" c="dimmed">
                    {formatDate(contentBlock.startDatetime)} -{" "}
                    {formatDate(contentBlock.endDatetime)}
                  </Text>
                )}
                {(contentBlock.startTime || contentBlock.endTime) && (
                  <Text size="xs" c="dimmed">
                    Time:{" "}
                    {formatTimeRange(
                      dayjs(contentBlock.startTime).format("hh:mm A"),
                      dayjs(contentBlock.endTime).format("hh:mm A")
                    )}
                  </Text>
                )}
                {contentBlock.recurrencePattern &&
                  contentBlock.recurrencePattern !== "NONE" && (
                    <Text size="xs" c="dimmed">
                      {contentBlock.recurrencePattern}
                      {contentBlock.recurrenceDays &&
                        `: ${formatDaysOfWeek(contentBlock.recurrenceDays)}`}
                    </Text>
                  )}
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Playlists List */}
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Select Playlists
          </Text>

          {isLoading ? (
            <Group justify="center" p="xl">
              <LoadingIndicator size={32} />
              <Text size="sm" c="dimmed">
                Loading playlists...
              </Text>
            </Group>
          ) : playLists.length === 0 ? (
            <Alert
              icon={<AppIcon iconName="InfoCircle" size={16} />}
              title="No playlists found"
              color="blue"
            >
              <Text size="sm">
                No playlists are available. Create a playlist first to add
                content blocks.
              </Text>
            </Alert>
          ) : (
            <ScrollArea.Autosize mah={400}>
              <Stack gap="xs">
                {playLists.map((playlist: any) => {
                  if (!playlist) {
                    return null;
                  }

                  const isAssigned = assignedPlaylistIds.has(playlist.id);
                  const operation = operations[playlist.id];
                  const isOperationLoading = operation?.loading || false;
                  const operationError = operation?.error || null;

                  return (
                    <Box key={playlist.id}>
                      <Group gap="sm" wrap="nowrap" p="xs" align="flex-start">
                        {isOperationLoading ? (
                          <LoadingIndicator size={18} />
                        ) : (
                          <Checkbox
                            checked={isAssigned}
                            onChange={(e) =>
                              handleCheckboxChange(
                                playlist.id,
                                e.currentTarget.checked
                              )
                            }
                            disabled={isOperationLoading}
                            size="sm"
                            mt={3}
                          />
                        )}

                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="md" fw={600} truncate>
                            {playlist.name || "Untitled Playlist"}
                          </Text>
                          {playlist.description && (
                            <Text size="xs" c="dimmed" truncate>
                              {playlist.description}
                            </Text>
                          )}
                        </Box>

                        {isAssigned && !isOperationLoading && (
                          <AppIcon iconName="Check" size={16} color="green" />
                        )}
                      </Group>

                      {operationError && (
                        <Alert
                          icon={<AppIcon iconName="AlertCircle" size={16} />}
                          color="red"
                          mt={4}
                          withCloseButton
                          onClose={() => clearPlaylistError(playlist.id)}
                        >
                          <Text size="xs">{operationError}</Text>
                        </Alert>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Stack>

        {/* Summary */}
        {playLists.length > 0 && !isLoading && (
          <Group gap="xs">
            <AppIcon iconName="InfoCircle" size={16} />
            <Text size="xs" c="dimmed">
              {assignedPlaylistIds.size} of {playLists.length} playlists
              selected
            </Text>
          </Group>
        )}
      </Stack>
    </AppModal>
  );
};
